import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable camelcase */
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CREATE_METHOD_OF_PAYMENT } from '../gql/createMop';
import { transformRecurlyMopDetailsToPaymentMethodsData } from '../utils/TransformUtils';
import { prependMopToList } from '../redux/MopOnFileState/MopOnFileState.actions';
import { updateDescription } from '../redux/MopOnFileState/MopOnFileState.helpers';
import { useAppSelector } from '../hooks/useAppSelector';
import { configSelector } from '../redux/ConfigState/ConfigState.selectors';
import { ErrorType, ValidationManager, StatusTypes, PaymentMethodsCommandNames, sendRioEvent, SubType, SpecType, APOLLO_COMMERCE_CONTEXT, PaymentMethodType, consolidateApolloErrors } from '@chegg/commerce-utils';
import { Component } from '../constants/CallbackConstants';
import { doFireStatusUpdate, sendRioEventCallback } from '../utils/CallbackUtils';
import { sendNewRelicMessage } from '../utils/NewRelicUtils';
import { createMOPError } from '../redux/PaymentState/PaymentState.actions';
import { GET_METHOD_OF_PAYMENT } from '../gql/getMop';
var getMopError = ValidationManager.ValidationMessages.getMopError;
export var MOCK_CREATE_MOP = {
  createMethodOfPayment: {
    id: 'v5cq8tc3bqfr',
    isBackup: false,
    isPrimary: false,
    mopHolderName: 'ecom test',
    mopInfo: {
      card: {
        binCountry: 'ZZ',
        cardType: 'VISA',
        expMonth: 4,
        expYear: 2025,
        last4: '1111'
      },
      type: PaymentMethodType.CREDIT_CARD,
      venmo: {
        username: undefined
      }
    },
    updatedAt: '2024-06-27T17:02:53.000Z',
    user: {
      uuid: '78f6ae0e-ee5b-4e6b-9ffd-6cc31be9c13c'
    },
    billingAddress: {
      addressLine1: '3990 Freedom Cir',
      addressLine2: null,
      city: 'Santa Clara',
      state: 'CA',
      postalCode: '95054',
      country: 'US',
      phone: null
    },
    valid: true
  }
};
export var getCommerceErrors = function getCommerceErrors(graphQLErrors) {
  return graphQLErrors.map(function (error) {
    var _ref = error.extensions.chegg || error.extensions,
      code = _ref.code,
      details = _ref.details;

    // Transform RCLY error code to pmt
    // Transforming to avoid updating a lot of error handling logic build for legacy
    var clientSideCodeMapping = code.replace('RCLY-', 'pmt');
    return {
      code: clientSideCodeMapping,
      type: ErrorType.SERVER,
      details: details
    };
  });
};
export var fireErrorEvents = function fireErrorEvents(errors, dispatch, rioNamespace) {
  var _errors$ = errors[0],
    code = _errors$.code,
    details = _errors$.details,
    type = _errors$.type;
  var message = details === null || details === void 0 ? void 0 : details.reason;
  var _getMopError = getMopError(code),
    message_detail = _getMopError.message_detail,
    message_id = _getMopError.message_id,
    message_name = _getMopError.message_name;
  var rioMessage = message || message_detail;
  // Callback
  doFireStatusUpdate([{
    status: StatusTypes.ERROR,
    command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
    payload: {
      component: Component.PAYMENT_MODULE_LOGIC,
      errors: [{
        code: message_id,
        message: message_detail,
        type: type
      }]
    }
  }]);

  // New Relic Logging
  sendNewRelicMessage("CreateMopLogic: ".concat(JSON.stringify(errors)));

  // Dispatch action to store errors
  dispatch(createMOPError({
    message: {
      text: message_detail,
      type: StatusTypes.ERROR,
      code: message_id
    }
  }));

  // Rio Logging
  sendRioEvent({
    info: message_name,
    specType: SpecType.NOTIFICATION,
    subType: SubType.ERROR,
    callback: sendRioEventCallback,
    namespace: rioNamespace,
    message_detail: "".concat(rioMessage),
    message_id: message_id
  });
};

/**
 * Returns the response from Create Mop mutation
 */
var UseCreateRecurlyMopHook = function UseCreateRecurlyMopHook(props) {
  var dispatch = useDispatch();
  var config = useAppSelector(configSelector);
  var paymentForm = config.paymentForm,
    rioNamespace = config.rioNamespace;
  var token = props.token,
    isPrimary = props.isPrimary;
  var _useMutation = useMutation(CREATE_METHOD_OF_PAYMENT, {
      variables: {
        createMethodOfPaymentInput: {
          recurlyBillingInfo: {
            tokenId: token,
            isPrimary: isPrimary
          }
        }
      },
      refetchQueries: refetchQueries,
      context: _defineProperty({}, APOLLO_COMMERCE_CONTEXT, true)
    }),
    _useMutation2 = _slicedToArray(_useMutation, 2),
    createRecurlyMop = _useMutation2[0],
    _useMutation2$ = _useMutation2[1],
    createMopData = _useMutation2$.data,
    createMopError = _useMutation2$.error;
  var createRecurlyMopForTokenId = function createRecurlyMopForTokenId(tokenId, isPrimary) {
    return createRecurlyMop({
      variables: {
        createMethodOfPaymentInput: {
          recurlyBillingInfo: {
            tokenId: tokenId,
            isPrimary: isPrimary
          }
        }
      }
    });
  };
  useEffect(function () {
    if (createMopData !== null && createMopData !== void 0 && createMopData.createMethodOfPayment) {
      var mop = transformRecurlyMopDetailsToPaymentMethodsData(createMopData === null || createMopData === void 0 ? void 0 : createMopData.createMethodOfPayment);
      dispatch(prependMopToList({
        mop: updateDescription(mop, paymentForm)
      }));
    }
  }, [createMopData]);
  useEffect(function () {
    if (createMopError !== null && createMopError !== void 0 && createMopError.graphQLErrors || createMopError !== null && createMopError !== void 0 && createMopError.networkError) {
      var errors = getCommerceErrors(consolidateApolloErrors(createMopError));
      fireErrorEvents(errors, dispatch, rioNamespace);
    }
  }, [createMopError]);
  return {
    createRecurlyMop: createRecurlyMop,
    createRecurlyMopForTokenId: createRecurlyMopForTokenId,
    createMopData: createMopData,
    createMopError: createMopError
  };
};
export var refetchQueries = function refetchQueries() {
  return [{
    query: GET_METHOD_OF_PAYMENT,
    context: _defineProperty({}, APOLLO_COMMERCE_CONTEXT, true)
  }];
};
export default UseCreateRecurlyMopHook;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/hooks/useCreateRecurlyMop.js
// module id = 897
// module chunks = 11